import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ChevronDown, ChevronUp } from "lucide-react";
import CopyField from "@/components/CopyField";

const empty = {
  enabled: false,
  android_package_name: "",
  android_sha256: "",
  play_store_url: "",
  ios_bundle_id: "",
  ios_team_id: "",
  app_store_url: "",
  notes: "",
};

const inputClass = "w-full mt-1 border border-neutral-300 px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-black";

// Same rule as mobile/build_app.py
const defaultAppId = (slug) => {
  let segment = slug.toLowerCase().replace(/[^a-z0-9]/g, "");
  if (!segment || /^\d/.test(segment)) segment = `t${segment}`;
  return `in.localappstore.services.${segment}`;
};

export default function SuperMobileApps() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/super/mobile-apps").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const toggle = (row) => {
    if (open === row.id) { setOpen(null); return; }
    setOpen(row.id);
    const saved = Object.fromEntries(Object.entries(row.mobile_app || {}).map(([k, v]) => [k, v ?? empty[k]]));
    setForm({ ...empty, ...saved });
  };

  const save = async (row) => {
    await api.put(`/super/tenants/${row.id}/mobile`, form);
    toast.success(`Saved for ${row.business_name}`);
    setOpen(null); load();
  };

  const field = (key, label, placeholder, hint) => (
    <div>
      <label className="admin-overline">{label}</label>
      <input value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value.trim() })} placeholder={placeholder} className={inputClass} />
      {hint && <div className="text-[11px] text-neutral-500 mt-1">{hint}</div>}
    </div>
  );

  return (
    <div data-testid="super-mobile-apps">
      <div className="admin-overline">Native</div>
      <h1 className="admin-heading text-3xl font-bold mt-1">Mobile apps</h1>
      <p className="text-sm text-neutral-600 mt-2 max-w-3xl">
        Each tenant's Android and iOS apps are built with <span className="font-mono">mobile/build_app.py</span>. The build reads the
        tenant's name, logo and colours from Branding, and the IDs below. Leave an ID blank to use the default shown. An app that is
        already in a store must keep its exact ID.
      </p>

      <div className="mt-6 space-y-3" data-testid="super-mobile-apps-list">
        {rows.map((row) => {
          const isOpen = open === row.id;
          const mobile = row.mobile_app || {};
          return (
            <div key={row.id} className="bg-white border border-neutral-200" data-testid={`super-mobile-row-${row.slug}`}>
              <div className="p-4 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  {row.branding?.logo_url
                    ? <img src={row.branding.logo_url} alt="" className="h-10 w-10 rounded object-cover border border-neutral-200 flex-shrink-0" />
                    : <div className="h-10 w-10 bg-neutral-200 flex items-center justify-center font-bold text-sm flex-shrink-0">{row.business_name[0]}</div>}
                  <div className="min-w-0">
                    <div className="font-semibold break-words">{row.business_name}</div>
                    <div className="text-xs text-neutral-500 font-mono break-anywhere">
                      Android: {mobile.android_package_name || `${defaultAppId(row.slug)} (default)`}
                    </div>
                    <div className="text-xs text-neutral-500 font-mono break-anywhere">
                      iOS: {mobile.ios_bundle_id || `${defaultAppId(row.slug)} (default)`}
                    </div>
                  </div>
                </div>
                <button onClick={() => toggle(row)} data-testid={`super-mobile-edit-${row.slug}`}
                  className="inline-flex items-center gap-1.5 border border-neutral-300 px-3 text-xs font-semibold uppercase tracking-wider hover:bg-neutral-50 min-h-[44px]">
                  {isOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />} {isOpen ? "Close" : "Configure"}
                </button>
              </div>

              {isOpen && (
                <div className="border-t border-neutral-200 bg-neutral-50 p-4 sm:p-6 space-y-5">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-white border border-neutral-200 p-4 space-y-3">
                      <div className="admin-overline">Android (Google Play)</div>
                      {field("android_package_name", "Application ID", defaultAppId(row.slug), "Blank uses the default.")}
                      {field("play_store_url", "Play Store URL (once live)", "https://play.google.com/store/apps/details?id=...")}
                    </div>
                    <div className="bg-white border border-neutral-200 p-4 space-y-3">
                      <div className="admin-overline">iOS (App Store)</div>
                      {field("ios_bundle_id", "Bundle ID", defaultAppId(row.slug), "Blank uses the default.")}
                      {field("ios_team_id", "Apple Team ID", "ABCDE12345", "Blank uses IOS_TEAM_ID from mobile/.env.build.")}
                      {field("app_store_url", "App Store URL (once live)", "https://apps.apple.com/in/app/...")}
                    </div>
                  </div>

                  <CopyField label="Build command (run inside mobile/)" testid={`super-mobile-build-${row.slug}`}
                    value={`python3 build_app.py --tenant ${row.slug} --app customer --version 1.0.0 --version-code 1 --platform both`}
                    hint="See BUILD.md for signing, versions and store upload." />

                  <details className="text-xs text-neutral-600">
                    <summary className="cursor-pointer font-semibold">Digital Asset Links — only for an older Trusted Web Activity app</summary>
                    <div className="mt-3 space-y-3">
                      <p>The apps built by build_app.py do not use these. Fill them in only if this tenant has a published TWA app that must keep verifying.</p>
                      <div>
                        <label className="admin-overline">SHA-256 fingerprint</label>
                        <textarea value={form.android_sha256 || ""} onChange={(e) => setForm({ ...form, android_sha256: e.target.value })} rows={2}
                          placeholder="AA:BB:CC:DD:..." className={`${inputClass} text-xs`} />
                      </div>
                      <label className="flex items-center gap-2 font-semibold min-h-[44px]">
                        <input type="checkbox" checked={!!form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} data-testid={`super-mobile-enabled-${row.slug}`} />
                        Include this tenant in /.well-known/assetlinks.json
                      </label>
                    </div>
                  </details>

                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => save(row)} data-testid={`super-mobile-save-${row.slug}`}
                      className="bg-black text-white px-5 py-2.5 text-xs font-semibold uppercase tracking-wider hover:bg-neutral-800 min-h-[44px]">Save</button>
                    <button onClick={() => setOpen(null)} className="border border-neutral-300 bg-white px-5 py-2.5 text-xs font-semibold uppercase tracking-wider min-h-[44px]">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
